import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import Header from './Header';
import Footer from './Footer';
import { theme } from '../../styles/theme';
import { useLocation } from 'react-router-dom';
import { scrollToWebsiteSection } from '../common/ScrollLink';

const WEBSITE_HEADER_HEIGHT = '69px';
const WEBSITE_HEADER_HEIGHT_MOBILE = '57px';
const WEBSITE_BANNER_HEIGHT = '72px';

const Main = styled.main`
  min-height: 100vh;
  padding-top: ${(props) =>
    props.showBanner
      ? `calc(${WEBSITE_HEADER_HEIGHT} + ${WEBSITE_BANNER_HEIGHT} + env(safe-area-inset-top, 0px))`
      : `calc(${WEBSITE_HEADER_HEIGHT} + env(safe-area-inset-top, 0px))`};
  background-color: #ffffff;
  transition: padding-top 0.3s ease;

  @media (max-width: 768px) {
    padding-top: ${(props) =>
      props.showBanner
        ? `calc(${WEBSITE_HEADER_HEIGHT_MOBILE} + ${WEBSITE_BANNER_HEIGHT} + env(safe-area-inset-top, 0px))`
        : `calc(${WEBSITE_HEADER_HEIGHT_MOBILE} + env(safe-area-inset-top, 0px))`};
  }
`;

const Layout = ({ children }) => {
  const location = useLocation();

  // Clear app theme preferences when accessing website to prevent dark mode bug
  useEffect(() => {
    try {
      // Clear theme-related localStorage items from main app
      localStorage.removeItem('themeMode');
      localStorage.removeItem('adminThemeMode');
      
      // Reset document theme attribute to ensure light mode
      document.documentElement.removeAttribute('data-theme');
      document.body.removeAttribute('data-theme');
      
      // Force light mode CSS variables if they were overridden
      const root = document.documentElement;
      root.style.removeProperty('--primary-color');
      root.style.removeProperty('--primary-contrast-color');
    } catch (error) {
      console.warn('Error clearing theme preferences:', error);
    }
  }, []); // Run once on mount

  // Always scroll to top on website page changes when not navigating to a hash target
  useEffect(() => {
    // Skip forcing top if navigating to a specific section via state or hash
    if (location.hash) return;
    // @ts-ignore location.state can be anything
    if (location.state && location.state.scrollToId) return;

    const forceTop = () => {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      } catch {}
    };

    // Immediate
    forceTop();
    // Next frame
    requestAnimationFrame(forceTop);
    // Retry briefly to overcome late layout/paint
    let attempts = 0;
    const max = 10;
    const id = setInterval(() => {
      attempts += 1;
      forceTop();
      if (attempts >= max) clearInterval(id);
    }, 50);

    return () => clearInterval(id);
  }, [location.pathname]);

  // Centralized scroll handling for hash or navigation state (e.g., from ScrollLink)
  useEffect(() => {
    const idFromHash = location.hash ? location.hash.replace('#', '') : undefined;
    const idFromState = location.state?.scrollToId;
    let idFromStorage;
    try { idFromStorage = sessionStorage.getItem('scrollToId') || undefined; } catch {}
    const targetId = idFromHash || idFromState || idFromStorage;
    if (!targetId) return;

    const tryScroll = () => scrollToWebsiteSection(targetId);

    // Attempt on next frame after route mount
    requestAnimationFrame(() => {
      if (tryScroll()) return;
    });

    let attempts = 0;
    const maxAttempts = 30; // ~1.5s total at 50ms
    const interval = setInterval(() => {
      attempts += 1;
      if (tryScroll() || attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 50);

    // Cleanup + clear storage marker after we attempt
    return () => {
      clearInterval(interval);
      try { sessionStorage.removeItem('scrollToId'); } catch {}
    };
  }, [location.pathname, location.hash, location.state]);

  return (
    <>
      <Header />
      <Main>{children}</Main>
      <Footer />
    </>
  );
};

export default Layout; 